import { CctvCameraGridDataModel } from 'api/models';
import { AnimatePresence, motion } from 'framer-motion';
import { Backdrop } from 'library/backdrop';
import { CrossIcon } from 'library/icons';
import OverlayLoader from 'modules/dashboard/components/ui/OverlayLoader';
import { CctvPlayer } from 'modules/dashboard/features/camera-cctv';
import useCctvStatus from 'modules/dashboard/hooks/use-cctv-status';
import { Zoomable } from 'modules/shared/features/zoomable';
import useBoxDimensions from 'modules/shared/hooks/useBoxDimensions';
import { FC, useMemo } from 'react';
import ScrollLock from 'react-scrolllock';

const modalAnimation = {
    variants: {
        hidden: {
            scale: 0.15,
            opacity: 0,
        },
        visible: {
            scale: 1,
            opacity: 1,
        },
    },
    transition: { duration: 0.35 },
    initial: 'hidden',
    animate: 'visible',
    exit: 'hidden',
};

/************************************************** components **************************************************/

interface GridWallSneakPeekModalProps {
    show: boolean;
    onClose: () => void;
    grid: CctvCameraGridDataModel;
    hlsUrl: string;
    webrtcUrl?: string;
}

const GridWallCctvSneakPeekModal: FC<GridWallSneakPeekModalProps> = (props) => {
    const { show, onClose, grid, hlsUrl, webrtcUrl } = props;

    const { aspectRatio, headerHeight, containerWidth, containerHeight } = useMemo(() => {
        const headerHeight = 60;

        return {
            aspectRatio: 1.49,
            headerHeight: 60,
            containerWidth: window.innerWidth - 96,
            containerHeight: window.innerHeight - 96 - headerHeight,
        };
    }, []);

    const { refs, dimensions } = useBoxDimensions({ aspectRatio, containerWidth, containerHeight });

    return (
        <AnimatePresence initial={false} mode="wait">
            {show && (
                <>
                    <Backdrop ref={refs.setReference} show={show} onClick={onClose} className="z-[30]" />

                    <ScrollLock>
                        <div
                            className="fixed left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] z-[31]"
                            style={{ height: dimensions.height + headerHeight, width: dimensions.width }}
                        >
                            <motion.div {...modalAnimation} onClick={(e) => e.stopPropagation()}>
                                <div
                                    className={`flex items-center bg-card-color px-6 rounded-t-2xl`}
                                    style={{ height: headerHeight }}
                                >
                                    <div className="flex items-center w-full gap-6">
                                        <h5 className="font-bold text-grayscale-900">
                                            {grid?.project?.name} - {grid?.cctvCamera?.name}
                                        </h5>
                                        <button
                                            className="ml-auto grid place-items-center w-7 h-7 rounded-full bg-background-color hover-transition hover:bg-primary/10"
                                            onClick={onClose}
                                        >
                                            <CrossIcon className="text-grayscale-500" width="10" height="10" />
                                        </button>
                                    </div>
                                </div>

                                <CctvSneakPeekPlayback
                                    key={JSON.stringify([grid?._id, hlsUrl, webrtcUrl])}
                                    hlsUrl={hlsUrl}
                                    webrtcUrl={webrtcUrl}
                                />
                            </motion.div>
                        </div>
                    </ScrollLock>
                </>
            )}
        </AnimatePresence>
    );
};

// Mount a fresh playback session on open and whenever the stream URLs change.
const CctvSneakPeekPlayback: FC<{ hlsUrl: string; webrtcUrl?: string }> = ({ hlsUrl, webrtcUrl }) => {
    const { isLoading, isOnline, isOffline, setOnline, setOffline } = useCctvStatus();

    return (
        <div className="relative">
            <Zoomable>
                <Zoomable.Container className="max-w-full max-h-full bg-card-color rounded-b-2xl">
                    <Zoomable.Content className="aspect-video max-w-full max-h-full">
                        {hlsUrl && (
                            <CctvPlayer
                                hlsUrl={hlsUrl}
                                webrtcUrl={webrtcUrl}
                                className="w-full h-full"
                                onData={setOnline}
                                onError={setOffline}
                            />
                        )}
                    </Zoomable.Content>
                </Zoomable.Container>

                {isOnline && (
                    <div className="flex items-center gap-2 absolute bottom-6 left-[50%] translate-x-[-50%] h-8 text-white rounded-[4px] text-xs px-3 bg-danger-600 font-semibold">
                        <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                        {'LIVE'}
                    </div>
                )}
            </Zoomable>

            {isOffline && (
                <div
                    role="status"
                    className="absolute inset-0 flex items-center justify-center rounded-b-2xl bg-card-color text-grayscale-900"
                >
                    Camera is offline. Retrying connection…
                </div>
            )}

            <OverlayLoader show={isLoading} className="rounded-b-2xl" />
        </div>
    );
};

export default GridWallCctvSneakPeekModal;
